import { useAuth0 } from '@auth0/auth0-react';
import { ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import Loading from './Loading';

interface AuthenticationGuardProps {
  children: ReactNode;
}

/**
 * AuthenticationGuard automatically redirects unauthenticated users to Auth0 login
 * while preserving their intended destination. Allows bypass for public routes.
 */
const AuthenticationGuard = ({ children }: AuthenticationGuardProps) => {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const location = useLocation();

  useEffect(() => {
    // Check if current route should bypass auto-login (e.g., landing page with ?public=true)
    const searchParams = new URLSearchParams(location.search);
    const shouldBypassAutoLogin =
      (location.pathname === '/' && searchParams.has('public')) ||
      location.pathname === '/terms' ||
      location.pathname.startsWith('/terms/');

    // Only redirect to login if not loading, not authenticated, and not on a public route
    if (!isLoading && !isAuthenticated && !shouldBypassAutoLogin) {
      loginWithRedirect({
        appState: {
          returnTo: location.pathname + location.search,
        },
      });
    }
  }, [isAuthenticated, isLoading, location, loginWithRedirect]);

  // Show loading during Auth0 initialization
  if (isLoading) {
    return (
      <div className="flex h-full flex-col justify-center">
        <Loading />
      </div>
    );
  }

  // If not authenticated and not on a public route, show loading
  // (user will be redirected to Auth0)
  const searchParams = new URLSearchParams(location.search);
  const isPublicRoute =
    (location.pathname === '/' && searchParams.has('public')) ||
    location.pathname === '/terms' ||
    location.pathname.startsWith('/terms/');

  if (!isAuthenticated && !isPublicRoute) {
    return (
      <div className="flex h-full flex-col justify-center">
        <Loading />
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthenticationGuard;
